import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const GuestCharts = ({ passes }) => {
  const chartData = useMemo(() => {
    // Country Data
    const countryMap = {};
    const statusMap = {};
    const professionMap = {};

    passes.forEach(p => {
      const c = p.country || 'Unknown';
      countryMap[c] = (countryMap[c] || 0) + 1;

      const s = p.status || 'unknown';
      statusMap[s] = (statusMap[s] || 0) + 1;

      const prof = p.profession || 'Unknown';
      professionMap[prof] = (professionMap[prof] || 0) + 1;
    });

    const countryData = Object.entries(countryMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Top 10 countries

    const statusData = Object.entries(statusMap).map(([name, value]) => ({ name, value }));

    const professionData = Object.entries(professionMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Top 10 professions

    return { countryData, statusData, professionData };
  }, [passes]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ background: 'var(--surface-1)', padding: '12px', border: '1px solid var(--border)', borderRadius: '8px', boxShadow: 'var(--shadow-md)' }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{label || payload[0].name}</p>
          <p style={{ margin: 0, color: payload[0].fill }}>Count: {payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <h2 style={{ marginBottom: '1.5rem' }}>Guest Data Visualizations</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
        {/* Country Bar Chart */}
        <div style={{ background: 'var(--surface-1)', padding: '24px', borderRadius: '12px', border: '1px solid var(--border)' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--text-1)' }}>Top 10 Countries</h3>
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData.countryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: 'var(--text-2)' }} axisLine={{ stroke: 'var(--border)' }} />
                <YAxis tick={{ fill: 'var(--text-2)' }} axisLine={{ stroke: 'var(--border)' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="var(--primary, #0f766e)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Pie Chart */}
        <div style={{ background: 'var(--surface-1)', padding: '24px', borderRadius: '12px', border: '1px solid var(--border)' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--text-1)' }}>Pass Status Breakdown</h3>
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData.statusData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {chartData.statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Profession Bar Chart */}
        <div style={{ background: 'var(--surface-1)', padding: '24px', borderRadius: '12px', border: '1px solid var(--border)', gridColumn: '1 / -1' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--text-1)' }}>Top 10 Professions</h3>
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData.professionData} layout="vertical" margin={{ left: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                <XAxis type="number" tick={{ fill: 'var(--text-2)' }} axisLine={{ stroke: 'var(--border)' }} />
                <YAxis dataKey="name" type="category" tick={{ fill: 'var(--text-2)' }} axisLine={{ stroke: 'var(--border)' }} width={120} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="#8884d8" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestCharts;
